import type { OrganizationType } from 'Model/Organization/OrganizationType.js'
import type { SampleConfigurationTypeKey } from '../../Generated/SampleConfigurationType.js'
import type { RedlistModeKey } from '../../Generated/RedlistMode.js'
import type { ProcessingTypeKey } from '../../Generated/ProcessingType.ts'
import type { PublicOrganization } from '../../Generated/PublicOrganization.ts'

export interface IUser {
  id: string
  firstName: string
  lastName: string
  email: string
  org: IOrganization
  authServer: string
  roles?: string[]
  isContact?: boolean
  showAdminUx?: boolean
  organizationOverrideId?: string
}

export type FeatureAccess = 'TIER1' | 'TIER2' | 'BETA'

export const FeatureAccesses = {
  TIER1: 'TIER1' as const,
  TIER2: 'TIER2' as const,
  BETA: 'BETA' as const
} as const

export interface ILicense {
  tier: LicenseTier
  featureAccesses: FeatureAccess[]
}

export enum LicenseTier {
  BASE = 'BASE',
  INSIGHT = 'INSIGHT',
  BETA = 'BETA'
}

export const Licenses: Record<LicenseTier, ILicense> = {
  [LicenseTier.BASE]: {
    tier: LicenseTier.BASE,
    featureAccesses: [FeatureAccesses.TIER1]
  },
  [LicenseTier.INSIGHT]: {
    tier: LicenseTier.INSIGHT,
    featureAccesses: [FeatureAccesses.TIER1, FeatureAccesses.TIER2]
  },
  [LicenseTier.BETA]: {
    tier: LicenseTier.BETA,
    featureAccesses: [FeatureAccesses.TIER1, FeatureAccesses.TIER2, FeatureAccesses.BETA]
  }
}

export function userLicense(user: IUser): ILicense | undefined {
  if (!user.org) return undefined
  return resolveLicenseFromPlan(user.org)
}

export function orgLicense(org: IOrganization): ILicense | undefined {
  return resolveLicenseFromPlan(org)
}

function resolveLicenseFromPlan(org: IOrganization): ILicense | undefined {
  if (isSubscriptionInactive(org) && org.isBillingMissing) {
    return undefined
  }

  if (!org.chargebeePlanId) return undefined

  const segments = org.chargebeePlanId.toLowerCase().split('-')
  if (segments.length < 5 || segments[0] !== 'spcfy') {
    console.warn('Unexpected Chargebee plan ID format', org.chargebeePlanId)
    return undefined
  }

  const license_raw = segments[1].toString()
  const license = Licenses[license_raw.toUpperCase() as LicenseTier]
  if (!license) {
    console.warn('unknown license tier: ', license)
  }
  return license
}

function resolvePaidFeatureAccessesFromPlan(org: IOrganization): FeatureAccess[] {
  return resolveFeatureAccessesFromEntitlements(org, org.chargebeeEntitlements ?? [])
}

const FEATURE_BY_ENTITLEMENT_ID: Record<string, FeatureAccess[]> = {
  tier2: [FeatureAccesses.TIER2],
  insight: [FeatureAccesses.TIER2],
  beta: [FeatureAccesses.TIER2, FeatureAccesses.BETA]
}

function resolveFeatureAccessesFromEntitlements(org: IOrganization, entitlements: string[]): FeatureAccess[] {
  if (isSubscriptionInactive(org) && org.isBillingMissing) {
    return []
  }
  const features = new Set<FeatureAccess>()
  for (const entitlement of entitlements) {
    const normalized = entitlement.trim().toLowerCase()
    const entitlementFeatures = FEATURE_BY_ENTITLEMENT_ID[normalized]
    if (entitlementFeatures) {
      entitlementFeatures.forEach((f) => features.add(f))
    }
  }
  return Array.from(features)
}

export function resolveFeatureAccesses(org: IOrganization): FeatureAccess[] {
  if (!org) return []

  return [FeatureAccesses.TIER1, ...resolvePaidFeatureAccessesFromPlan(org)]
}

export function hasFeatureAccess(org: IOrganization, featureAccess: FeatureAccess): boolean {
  if (!org) return false
  return resolveFeatureAccesses(org).includes(featureAccess)
}

export function isGlobalAdmin(user?: IUser): boolean {
  return user?.roles?.includes('global-administrator') ?? false
}

/** True when the user has the Keycloak `chat-beta` realm role (emitted in the token's `roles` claim,
 * mirroring `global-administrator`). The backend derives `RoleNames.CHAT_BETA` from the same role's
 * `/role::chat-beta` group entry. Gates the chat/agent feature's routes + nav entry points on the frontend. */
export function isChatBeta(user?: IUser): boolean {
  return user?.roles?.includes('chat-beta') ?? false
}

export type CommercialType = 'COMMERCIAL' | 'ACADEMIC'
export const SubscriptionStatuses = {
  ACTIVE: 'active',
  NON_RENEWING: 'non_renewing',
  CANCELLED: 'cancelled',
  PAUSED: 'paused',
  DELETED: 'deleted',
  IN_TRIAL: 'in_trial'
} as const

export type SubscriptionStatus = (typeof SubscriptionStatuses)[keyof typeof SubscriptionStatuses]

export function isSubscriptionStatus(value: unknown): value is SubscriptionStatus {
  return typeof value === 'string' && Object.values(SubscriptionStatuses).includes(value as SubscriptionStatus)
}

export function normalizeSubscriptionStatus(value: unknown): SubscriptionStatus | undefined {
  return isSubscriptionStatus(value) ? value : undefined
}

export interface IOrganization {
  name: string
  id: string
  /** True for internal/test orgs (e.g. our own testing org, a power-user lab). Derived from the
   * Keycloak org attribute `org_internal`; used to exclude noise from product-analytics metrics. */
  isInternal?: boolean
  type: OrganizationType
  description?: string
  isPublic: boolean
  defaultSampleConfigurationType: SampleConfigurationTypeKey
  defaultRedlistMode: RedlistModeKey
  defaultProcessingType: ProcessingTypeKey
  homepage?: string
  country?: string
  specialities?: string[]
  logo?: string
  contactEmail?: string
  isPending?: boolean
  isBillingMissing: boolean
  chargebeeCustomerId?: string
  chargebeePlanId?: string
  chargebeeSubscriptionId?: string
  lastPaymentIssueAt?: string
  chargebeeSubscriptionStatus?: SubscriptionStatus
  chargebeeTrialEndsAt?: string
  commercialType?: CommercialType
  chargebeeEntitlements?: string[]
  chargebeeEntitlementsSyncedAt?: string
}

/**
 * An organisation as something that merely has to be named and badged — the full org from
 * `/api/organizations/{id}`, or the narrow projection the anonymous share link returns.
 *
 * Anything reading an org out of the order slice's cache gets this, because that cache is filled from
 * both. Widen to `IOrganization` only where the commercial fields are genuinely needed, and never on a
 * path an anonymous share-link viewer can reach.
 */
export type IOrganizationRef = IOrganization | PublicOrganization

export function hasActiveSubscription(org: IOrganization): boolean {
  return (
    !!org.chargebeePlanId &&
    (org.chargebeeSubscriptionStatus === SubscriptionStatuses.ACTIVE ||
      org.chargebeeSubscriptionStatus === SubscriptionStatuses.NON_RENEWING ||
      org.chargebeeSubscriptionStatus === SubscriptionStatuses.IN_TRIAL)
  )
}

/** True when the org had a Chargebee subscription that is no longer active */
export function isSubscriptionInactive(org: IOrganization): boolean {
  if (!org.chargebeeSubscriptionId) return false
  return !hasActiveSubscription(org)
}


export interface ITokenInfoOrganizationDetail {
  id: string
  /**
   * Keycloak organisation attributes always arrive as string arrays — `org_type` is no exception,
   * despite once being typed as a bare enum here. Unwrap it with `firstOrNull` like every sibling
   * attribute; assigning the raw array to `IOrganization.type` silently broke every
   * `org.type === …` comparison and made the value undeliverable as a telemetry attribute.
   */
  org_type?: OrganizationType[]
  org_internal?: string[]
  org_is_public?: boolean
  licenses?: LicenseTier[]
  'org::sample_config'?: SampleConfigurationTypeKey[]
  'org::default_redlist_mode'?: RedlistModeKey[]
  'org::default_processing_type'?: ProcessingTypeKey[]
  org_pending: string[]
  org_billing_missing: string[]
  chargebee_customer_id?: string[]
  chargebee_plan_id?: string[]
  chargebee_subscription_id?: string[]
  chargebee_last_payment_issue_at?: string[]
  chargebee_subscription_status?: ('active' | 'cancelled' | 'non_renewing' | 'paused' | 'in_trial' | string)[]
  chargebee_trial_ends_at?: string[]
  org_commercial_type?: ('COMMERCIAL' | 'ACADEMIC')[]
  chargebee_entitlements?: string[]
}

export interface ITokenInfoResponse {
  at_hash: string
  email_verified: boolean
  roles: string[]
  typ: string
  preferred_username: string
  given_name: string
  nonce: string
  sid: string
  acr: string
  azp: string
  auth_time: number
  organization: Record<string, ITokenInfoOrganizationDetail>
  name: string
  family_name: string
  email: string
  group: string[]
  show_admin_ux?: string | string[]
  organization_override_id?: string
  active: boolean
  username: string
  exp: number
  iat: number
  sub: string
  aud: string
  iss: string
  jti: string
}
